import { useState } from 'react';

import moment from 'moment';
import { Link, useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { RunSummaryDto } from '../../../../../../../backend/src/case/subset/dto/run-summary.dto';
import { RunStatus } from '../../../../../../../backend/src/types/types';
import { useSubmitRunApproval } from '@/api/mutations/useSubmitRunApproval';
import { useGetFailedDocs } from '@/api/queries/useGetFailedDocs';
import formatDate from '@/helpers/formatDate';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { formatDurationEstimate } from '@/new/components/processing-status-badge';
import { RunApprovalPopoverContent } from '@/new/components/run-approval-popover-content';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent } from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { isProcessingStatus, PipelineProcessingStageDescriptions, ProcessingStatus } from '@/new/lib/pipeline-status';

interface RecentRunStatusProps {
  mostRecentRun: RunSummaryDto | null;
  allDocumentsCaseId: string | undefined;
}

export function RecentRunStatus({ mostRecentRun, allDocumentsCaseId }: RecentRunStatusProps) {
  const { matterId } = useParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { hasPermission } = useHasPermission();

  // Check if user has permission to approve runs
  const canApproveRuns = hasPermission(Permission.ApproveRuns);

  // hooks
  const { mutateAsync: submitRunApproval, isLoading: isSubmittingApproval } = useSubmitRunApproval(
    allDocumentsCaseId,
    matterId,
  );
  const { data: failedDocs } = useGetFailedDocs(mostRecentRun?.id);

  const handleApprove = async () => {
    if (!mostRecentRun) return;
    await submitRunApproval({ runId: mostRecentRun.id, approvalGranted: true });
    setIsDialogOpen(false);
  };

  const handleDeny = async () => {
    if (!mostRecentRun) return;
    await submitRunApproval({ runId: mostRecentRun.id, approvalGranted: false });
    setIsDialogOpen(false);
  };

  if (!mostRecentRun) return null;

  const isCompleted = !!mostRecentRun.completedAt;
  const isAwaitingApproval = mostRecentRun.status === RunStatus.AwaitingApproval;
  const isProcessing = !isCompleted && isProcessingStatus(mostRecentRun.status as RunStatus);

  // Fetch the number of failed docs with this run id
  const failedDocsCount = failedDocs?.length ?? 0;

  // If the run is awaiting approval, show the approval status
  if (isAwaitingApproval) {
    const pageCount = mostRecentRun.totalPages ?? 0;
    const pageText = pageCount === 1 ? '1 Page' : `${pageCount.toLocaleString()} Pages`;
    const credits =
      (mostRecentRun.totalPages as number | undefined) ??
      (mostRecentRun.successfulPagesCount || 0) + (mostRecentRun.failedPagesCount || 0);
    const requiresAdminApproval = mostRecentRun.requiresAdminApproval ?? false;
    const durationText = formatDurationEstimate(mostRecentRun.durationEstimate);

    const approvalButton = (
      <Button
        size="sm"
        variant="outline"
        className="border-amber-200 bg-amber-100 text-xs text-amber-600 hover:border-amber-300 hover:bg-amber-200 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => setIsDialogOpen(true)}
        disabled={!canApproveRuns}
      >
        Approve/Cancel Run
        <Icon className="lucide--arrow-right" />
      </Button>
    );

    console;

    return (
      <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 pr-4">
        <div className="flex gap-2">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Icon
                className={
                  requiresAdminApproval ? 'lucide--shield-check text-amber-900' : 'lucide--shield-alert text-amber-900'
                }
              />
              <h3 className="text-xs font-medium text-amber-900">
                {requiresAdminApproval ? 'Admin Approval Required' : 'Approval Required'}
              </h3>
            </div>
            <p className="flex gap-2 text-xs text-amber-700">
              {pageCount > 0 ? pageText : 'Pages'} pending approval ({credits.toLocaleString?.()} credits)
              {durationText && (
                <>
                  <span className="font-extralight">•</span>
                  <span>Est. {durationText}</span>
                </>
              )}
            </p>
            {canApproveRuns ? (
              approvalButton
            ) : (
              <Tooltip>
                <TooltipTrigger>{approvalButton}</TooltipTrigger>
                <TooltipContent>You don't have permission to approve runs</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="w-96 max-w-96 p-0">
            <RunApprovalPopoverContent
              credits={credits}
              durationEstimate={mostRecentRun.durationEstimate}
              isSubmittingApproval={isSubmittingApproval}
              onApprove={handleApprove}
              onDeny={handleDeny}
              requiresAdminApproval={requiresAdminApproval}
              canApprove={canApproveRuns}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // If the run is still processing, show the processing status
  if (isProcessing) {
    const processingStatus = mostRecentRun.status as ProcessingStatus;
    const statusInfo = PipelineProcessingStageDescriptions[processingStatus];
    const pageCount = mostRecentRun.totalPages ?? 0;
    const pageText = pageCount === 1 ? '1 Page' : `${pageCount.toLocaleString()} Pages`;

    return (
      <div className="rounded-md border border-sky-300 bg-sky-50 p-3 pr-4">
        <div className="flex gap-2">
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Icon className="lucide--loader-circle animate-spin text-sky-700 duration-1000" />
              <h3 className="text-xs font-medium text-sky-800">Processing {pageCount > 0 ? pageText : 'Pages'}</h3>
            </div>
            <div className="flex gap-2 text-xs text-sky-700">
              <p>{statusInfo.subTitle}</p>
              <span className="font-extralight text-sky-700">•</span>
              <Link
                className="flex items-center gap-1 text-blue-500 duration-150 hover:text-blue-600"
                to={`/matters/${matterId}/settings/run-history`}
              >
                <span>View details</span>
                <Icon className="lucide--arrow-right" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If the run is completed
  if (isCompleted) {
    const completedTime = mostRecentRun.completedAt ? formatDate(mostRecentRun.completedAt, true, true) : '';

    // Show failed pages warning if there are any
    if (failedDocsCount > 0) {
      if (!mostRecentRun.completedAt) {
        return null;
      }

      const completedDate = new Date(mostRecentRun.completedAt);

      return (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 pr-4">
          <div className="flex gap-2">
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Icon className="lucide--circle-alert text-amber-600" />
                <h3 className="text-xs font-medium text-amber-900">Previous Run Completed with Issues</h3>
              </div>
              <div className="flex gap-2 text-xs text-amber-800">
                <p>
                  {failedDocsCount.toLocaleString()} {failedDocsCount === 1 ? 'document' : 'documents'} failed to
                  process
                  {completedTime && ` ${moment(completedDate).fromNow()} (${completedTime})`}
                </p>
                <span className="font-extralight">•</span>
                <Link
                  className="flex items-center gap-1 duration-150 hover:text-amber-700"
                  to={`/matters/${matterId}/subsets/${allDocumentsCaseId}/docs?processed=false`}
                >
                  <span>View documents</span>
                  <Icon className="lucide--arrow-right" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show success message if no failed pages and completed within the last week
    if (!mostRecentRun.completedAt) {
      return null;
    }

    const completedDate = new Date(mostRecentRun.completedAt);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    if (completedDate < oneWeekAgo) {
      return null;
    }

    const pageCount = mostRecentRun.totalPages ?? 0;
    const pageText = pageCount === 1 ? '1 Page' : `${pageCount.toLocaleString()} Pages`;

    return (
      <div className="rounded-md border border-gray-200 bg-gray-50/50 p-3 pr-4">
        <div className="flex gap-2">
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Icon className="lucide--circle-check text-green-500" />
              <h3 className="text-xs font-medium text-gray-900">Recent Run Finished Processing</h3>
            </div>
            <div className="flex gap-2 text-xs text-gray-600">
              <p>
                {pageCount > 0 ? `${pageText} processed successfully` : 'All Pages processed successfully'}
                {completedTime && ` ${moment(completedDate).fromNow()} (${completedTime})`}
              </p>
              <span className="font-extralight text-gray-500">•</span>
              <Link
                className="flex items-center gap-1 text-blue-500 duration-150 hover:text-blue-600"
                to={`/matters/${matterId}/settings/run-history`}
              >
                <span>View run details</span>
                <Icon className="lucide--arrow-right" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
